import { useEffect, useRef, useState, useCallback } from 'react';
import { loadGoogleMapsLibrary } from '@/lib/google-maps-loader';
import { useRouteCalculation } from '@/hooks/useRouteCalculation';

interface DynamicMapProps {
  destination: { lat: number; lng: number };
  filmmakerLocation: { lat: number; lng: number } | null;
  mode?: 'filmmaker' | 'customer';
  className?: string;
  height?: string;
  onMapReady?: (map: google.maps.Map) => void;
}

// Type definitions for AdvancedMarkerElement
interface AdvancedMarkerElement extends HTMLElement {
  position?: google.maps.LatLng | google.maps.LatLngLiteral;
  map?: google.maps.Map | null;
  title?: string;
  zIndex?: number;
  gmpClickable?: boolean;
}

interface PinElement extends HTMLElement {
  background?: string;
  borderColor?: string;
  glyph?: string | Element | URL;
  glyphColor?: string;
  scale?: number;
}

/**
 * DynamicMap component that displays destination, filmmaker location, and route
 * Updates in real-time as filmmaker location changes
 * Uses AdvancedMarkerElement (new API) instead of deprecated Marker
 */
export function DynamicMap({
  destination,
  filmmakerLocation,
  mode = 'customer',
  className = '',
  height = '400px',
  onMapReady,
}: DynamicMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const destinationMarkerRef = useRef<AdvancedMarkerElement | null>(null);
  const filmmakerMarkerRef = useRef<AdvancedMarkerElement | null>(null);
  const routePolylineRef = useRef<google.maps.Polyline | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const hasFittedBoundsRef = useRef(false); // Track if we've already fitted bounds
  const markerLibraryRef = useRef<{
    AdvancedMarkerElement: typeof AdvancedMarkerElement;
    PinElement: typeof PinElement;
  } | null>(null);
  const mapIdRef = useRef<string | null>(null); // Store mapId to prevent re-initialization
  const isInitializingRef = useRef(false); // Prevent concurrent initializations

  // Calculate route when filmmaker location is available
  const { route, eta, etaMinutes, loading: routeLoading } = useRouteCalculation({
    origin: filmmakerLocation,
    destination: filmmakerLocation ? destination : null,
    enabled: !!filmmakerLocation,
    travelMode: 'DRIVING',
  });

  // Initialize map and load marker library (only once)
  useEffect(() => {
    // Prevent multiple initializations
    if (!mapRef.current || mapInstanceRef.current || isInitializingRef.current) {
      return;
    }

    // Get Map ID from environment once and store it
    if (!mapIdRef.current) {
      mapIdRef.current = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
    }

    isInitializingRef.current = true;

    const initMap = async () => {
      try {
        // Double check mapRef is still available
        if (!mapRef.current) {
          isInitializingRef.current = false;
          setMapLoaded(true);
          return;
        }

        // Load maps library first
        await loadGoogleMapsLibrary('maps');

        // Wait for Google Maps to be fully available with retries
        let retries = 0;
        const maxRetries = 50; // 5 seconds total (100ms * 50)
        while (!window.google?.maps?.Map && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!window.google?.maps?.Map) {
          throw new Error('Google Maps Map class not available after loading');
        }

        // Load marker library
        await loadGoogleMapsLibrary('marker');

        // Wait a bit for marker library to be fully loaded
        await new Promise(resolve => setTimeout(resolve, 300));

        // Load marker library classes
        if (!window.google?.maps?.importLibrary) {
          throw new Error('Google Maps importLibrary not available');
        }

        const markerLib = await window.google.maps.importLibrary('marker') as any;
        
        if (!markerLib?.AdvancedMarkerElement || !markerLib?.PinElement) {
          throw new Error('AdvancedMarkerElement or PinElement not available');
        }
        
        markerLibraryRef.current = {
          AdvancedMarkerElement: markerLib.AdvancedMarkerElement,
          PinElement: markerLib.PinElement,
        };

        // Double check mapRef is still available
        if (!mapRef.current) {
          throw new Error('mapRef is null after loading libraries');
        }

        // Center map on destination initially (use current destination value)
        const center = destination;

        // Use stored mapId (read once at the beginning) - this prevents re-initialization with different mapId
        const mapId = mapIdRef.current!;

        const map = new window.google.maps.Map(mapRef.current, {
          center,
          zoom: 13,
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          mapId, // Required for AdvancedMarkerElement - use stored mapId to prevent re-initialization
          minZoom: 2,
          maxZoom: 20,
        });

        mapInstanceRef.current = map;
        setMapLoaded(true);
        isInitializingRef.current = false;

        if (onMapReady) {
          onMapReady(map);
        }

        // Create destination marker using AdvancedMarkerElement
        const destinationPin = new markerLib.PinElement({
          background: '#ef4444',
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.2,
        });

        destinationMarkerRef.current = new markerLib.AdvancedMarkerElement({
          map,
          position: destination,
          title: 'Destination',
          content: destinationPin.element,
          zIndex: 1000,
        });
      } catch (error) {
        console.error('Error initializing map:', error);
        isInitializingRef.current = false;
        setMapLoaded(true);
      }
    };

    // Wait for mapRef to be available
    if (!mapRef.current) {
      const timer = setTimeout(() => {
        if (mapRef.current && !mapInstanceRef.current && !isInitializingRef.current) {
          initMap();
        } else {
          isInitializingRef.current = false;
        }
      }, 300);
      return () => {
        clearTimeout(timer);
        isInitializingRef.current = false;
      };
    }

    // mapRef is available, initialize immediately
    initMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - initialize only once, destination is captured in closure

  // Update destination marker when destination changes (after map is initialized)
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || !markerLibraryRef.current || !destinationMarkerRef.current) return;

    // Update destination marker position if destination changes
    destinationMarkerRef.current.position = destination;
  }, [destination, mapLoaded]);

  // Update filmmaker marker when location changes
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || !markerLibraryRef.current) return;

    const map = mapInstanceRef.current;
    const { AdvancedMarkerElement, PinElement } = markerLibraryRef.current;

    if (filmmakerLocation) {
      // Create or update filmmaker marker
      if (!filmmakerMarkerRef.current) {
        const filmmakerPin = new PinElement({
          background: '#3b82f6', // blue-500
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.0,
        });

        filmmakerMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: filmmakerLocation,
          title: 'Filmmaker Location',
          content: filmmakerPin.element,
          zIndex: 1001,
        });

        // Only fit bounds once when filmmaker marker is first created
        // This allows user to move the map freely afterwards
        if (!hasFittedBoundsRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(destination);
          bounds.extend(filmmakerLocation);
          
          // Calculate approximate distance in degrees (rough estimate)
          const latDiff = Math.abs(destination.lat - filmmakerLocation.lat);
          const lngDiff = Math.abs(destination.lng - filmmakerLocation.lng);
          const maxDiff = Math.max(latDiff, lngDiff);
          
          // Use adaptive padding: larger distances get more padding, but cap it
          // For very large distances (e.g., intercontinental), use fixed padding
          const padding = maxDiff > 1 ? 100 : Math.min(Math.max(maxDiff * 200, 50), 150);
          
          // Fit bounds with padding only once
          map.fitBounds(bounds, { padding });
          hasFittedBoundsRef.current = true;
        }
      } else {
        // Just update the position, don't refit bounds
        // This allows the user to move the map freely
        filmmakerMarkerRef.current.position = filmmakerLocation;
      }
    } else {
      // Remove filmmaker marker if location is not available
      if (filmmakerMarkerRef.current) {
        filmmakerMarkerRef.current.map = null;
        filmmakerMarkerRef.current = null;
        hasFittedBoundsRef.current = false; // Reset flag if marker is removed
      }
    }
  }, [filmmakerLocation, mapLoaded, destination]);

  // Update route polyline when route is calculated
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || !route?.polyline || !filmmakerLocation) return;

    const map = mapInstanceRef.current;

    const renderRoute = async () => {
      try {
        // Load geometry library for polyline decoding
        await loadGoogleMapsLibrary('geometry');

        // Decode polyline using geometry library (Directions API uses encoded polyline)
        if (window.google.maps.geometry?.encoding) {
          const decodedPath = window.google.maps.geometry.encoding.decodePath(route.polyline!);

          // Create or update polyline
          if (!routePolylineRef.current) {
            routePolylineRef.current = new window.google.maps.Polyline({
              map,
              path: decodedPath,
              geodesic: true,
              strokeColor: '#3b82f6', // blue-500
              strokeOpacity: 0.8,
              strokeWeight: 4,
              zIndex: 500,
            });
          } else {
            routePolylineRef.current.setPath(decodedPath);
          }
        } else {
          throw new Error('Geometry encoding not available');
        }
      } catch (error) {
        console.error('Error rendering route with polyline, using fallback:', error);
        // Fallback: create simple line between origin and destination
        const simplePath = [
          new window.google.maps.LatLng(filmmakerLocation.lat, filmmakerLocation.lng),
          new window.google.maps.LatLng(destination.lat, destination.lng),
        ];

        if (!routePolylineRef.current) {
          routePolylineRef.current = new window.google.maps.Polyline({
            map,
            path: simplePath,
            geodesic: true,
            strokeColor: '#3b82f6',
            strokeOpacity: 0.6,
            strokeWeight: 3,
            zIndex: 500,
          });
        } else {
          routePolylineRef.current.setPath(simplePath);
        }
      }
    };

    renderRoute();
  }, [route, mapLoaded, filmmakerLocation, destination]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
      }
      if (filmmakerMarkerRef.current) {
        filmmakerMarkerRef.current.map = null;
      }
      if (routePolylineRef.current) {
        routePolylineRef.current.setMap(null);
      }
    };
  }, []);

  return (
    <div className={`relative ${className}`} style={{ height }}>
      <div ref={mapRef} className="w-full h-full rounded-lg overflow-hidden border" style={{ display: mapLoaded ? 'block' : 'none' }} />
      {!mapLoaded && (
        <div className="absolute inset-0 w-full h-full rounded-lg overflow-hidden border bg-muted/50 flex items-center justify-center z-10">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}
      {routeLoading && filmmakerLocation && mapLoaded && (
        <div className="absolute top-2 left-2 bg-background/90 px-2 py-1 rounded text-xs z-20">
          Calculating route...
        </div>
      )}
    </div>
  );
}


